// Core imports
import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { Button, Select, Input, InputNumber, Space, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Ant Design dropdown menus must sit above the MUI DataGrid panel (~z-index 1300)
const DROPDOWN_STYLE = { zIndex: 1500 };

const OPERATORS_BY_TYPE = {
  string: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' }
  ],
  singleSelect: [
    { value: 'is', label: 'is' },
    { value: 'not', label: 'is not' },
    { value: 'isAnyOf', label: 'is any of' }
  ],
  boolean: [
    { value: 'is', label: 'is' }
  ],
  number: [
    { value: '=', label: '=' },
    { value: '!=', label: '≠' },
    { value: '>', label: '>' },
    { value: '>=', label: '≥' },
    { value: '<', label: '<' },
    { value: '<=', label: '≤' }
  ]
};

const BOOLEAN_VALUE_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

let _nextId = 1;

// filterableColumns   — list of column definitions (field, headerName, type, filterOperators, valueOptions)
// onFilterModelChange — NdesTable's handler; called directly to bypass MUI's single-filter restriction
// filterModel         — current filter model from NdesTable state; used to restore prior filters on mount
const MultiFilterPanel = ({ filterableColumns = [], onApplyFilters, filterModel }) => {

  // Initialise from the current filterModel so reopening the panel restores prior filters
  const [items, setItems] = useState(() =>
    (filterModel?.items || []).map(item => ({ ...item, _id: item.id || _nextId++ }))
  );
  // Keep a mutable ref in sync so closures always read the latest items
  const itemsRef = useRef(items);

  // Push valid (non-empty) items to NdesTable via the onFilterModelChange prop,
  // bypassing apiRef.setFilterModel() which MUI trims to 1 item in the free tier.
  // Each item includes an `id` as required by MUI when multiple filters are present.
  const applyToGrid = (newItems) => {
    const valid = newItems
      .filter(i =>
        i.field
        && i.value != null
        && i.value !== ''
        && !(Array.isArray(i.value) && i.value.length === 0)
      )
      .map(({ _id, ...rest }) => ({ ...rest, id: _id }));
    onApplyFilters({ items: valid });

  };

  const updateItems = (newItems) => {
    itemsRef.current = newItems;
    setItems(newItems);
    applyToGrid(newItems);
  };

  const getCol = (field) => filterableColumns.find(c => c.field === field);

  const getOperators = (col) => {
    if (!col) {
      return OPERATORS_BY_TYPE.string;
    }
    // Column-specific filterOperators take precedence
    if (col.filterOperators?.length) {
      return col.filterOperators.map(op => ({ value: op.value, label: op.label || op.value }));
    }
    return OPERATORS_BY_TYPE[col.type || 'string'] || OPERATORS_BY_TYPE.string;
  };

  const addFilter = () => {
    const col = filterableColumns[0];
    const operators = getOperators(col);
    updateItems([...itemsRef.current, {
      _id: _nextId++,
      field: col?.field || '',
      operator: operators[0]?.value || 'contains',
      value: ''
    }]);
  };

  const removeFilter = (id) => updateItems(itemsRef.current.filter(i => i._id !== id));

  const patchItem = (id, patch) => {
    updateItems(itemsRef.current.map(i => i._id === id ? { ...i, ...patch } : i));
  };

  const handleFieldChange = (id, field) => {
    const col = getCol(field);
    const operators = getOperators(col);
    patchItem(id, { field, operator: operators[0]?.value || 'contains', value: '' });
  };

  const renderValueInput = (item) => {
    const col = getCol(item.field);
    const type = col?.type || 'string';

    if (type === 'singleSelect') {
      const opts = col.valueOptions || [];
      if (item.operator === 'isAnyOf') {
        return (
          <Select
            mode="multiple"
            size="small"
            value={Array.isArray(item.value) ? item.value : (item.value ? [item.value] : [])}
            onChange={v => patchItem(item._id, { value: v })}
            options={opts}
            style={{ width: 200 }}
            placeholder="Select values"
            popupMatchSelectWidth={false}
            dropdownStyle={DROPDOWN_STYLE}
          />
        );
      }
      return (
        <Select
          size="small"
          value={item.value || undefined}
          onChange={v => patchItem(item._id, { value: v ?? '' })}
          options={opts}
          style={{ width: 200 }}
          placeholder="Select value"
          allowClear
          popupMatchSelectWidth={false}
          dropdownStyle={DROPDOWN_STYLE}
        />
      );
    }

    if (type === 'boolean') {
      return (
        <Select
          size="small"
          value={item.value || undefined}
          onChange={v => patchItem(item._id, { value: v ?? '' })}
          options={BOOLEAN_VALUE_OPTIONS}
          style={{ width: 100 }}
          placeholder="Select"
          allowClear
          dropdownStyle={DROPDOWN_STYLE}
        />
      );
    }

    if (type === 'number') {
      return (
        <InputNumber
          size="small"
          value={item.value !== '' ? Number(item.value) : undefined}
          onChange={v => patchItem(item._id, { value: v ?? '' })}
          style={{ width: 100 }}
          placeholder="Value"
        />
      );
    }

    // Default: string
    return (
      <Input
        size="small"
        value={item.value}
        onChange={e => patchItem(item._id, { value: e.target.value })}
        style={{ width: 200 }}
        placeholder="Filter value"
        allowClear
        onClear={() => patchItem(item._id, { value: '' })}
      />
    );
  };

  return (
    <div style={{ padding: '16px 16px 12px', minWidth: 520 }}>
      {items.length === 0 && (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>
          No filters applied.
        </Text>
      )}
      {items.map(item => {
        const col = getCol(item.field);
        const operators = getOperators(col);
        return (
          <Space key={item._id} style={{ display: 'flex', marginBottom: 8 }} align="center">
            <Select
              size="small"
              value={item.field || undefined}
              onChange={v => handleFieldChange(item._id, v)}
              options={filterableColumns.map(c => ({ value: c.field, label: c.headerName }))}
              style={{ width: 180 }}
              showSearch
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              placeholder="Column"
              popupMatchSelectWidth={false}
              dropdownStyle={DROPDOWN_STYLE}
            />
            <Select
              size="small"
              value={item.operator}
              onChange={v => patchItem(item._id, { operator: v, value: '' })}
              options={operators}
              style={{ width: 110 }}
              dropdownStyle={DROPDOWN_STYLE}
            />
            {renderValueInput(item)}
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeFilter(item._id)}
              title="Remove filter"
            />
          </Space>
        );
      })}
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={addFilter}
        style={{ marginTop: items.length > 0 ? 4 : 0 }}
      >
        Add filter
      </Button>
    </div>
  );
};

MultiFilterPanel.propTypes = {
  filterableColumns: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    headerName: PropTypes.string.isRequired,
    type: PropTypes.string,
    filterOperators: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string
    })),
    valueOptions: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired
    }))
  })).isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  filterModel: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      field: PropTypes.string.isRequired,
      operator: PropTypes.string.isRequired,
      value: PropTypes.any
    }))
  })
};

export default MultiFilterPanel;
